<template>
  <v-list-item
    v-if="canViewVerticalNavMenuLink(item)"
    v-bind="linkProps"
    :class="{'bg-gradient-primary white--text': isActive}"
    class="vertical-nav-menu-link"
    active-class="bg-gradient-primary"
  >
    <v-list-item-icon>
      <v-icon
        :class="{'alternate-icon-small': !item.icon}"
        :color="isActive ? 'white': null"
      >
        {{ item.icon || alternateIcon }}
      </v-icon>
    </v-list-item-icon>

    <v-list-item-title :class="{'white--text': isActive}">
      {{ t(item.title) }}
    </v-list-item-title>
    <v-list-item-action
      v-if="item.title == 'Requests' && item.badge && item.show.includes(userRole)"
      class="flex-shrink-0"
    >
      <transition
        name="badge-fade"
        mode="out-in"
      >
        <v-badge
          v-if="pendingTotal > 0"
          :key="pendingTotal"
          :color="item.badgeColor"
          inline
          :content="pendingTotal"
          :value="pendingTotal"
        >
        </v-badge>
      </transition>
    </v-list-item-action>
    <v-list-item-action
      v-else-if="item.title == 'SPD Reporting' && item.badge && item.show.includes(userRole)"
      class="flex-shrink-0"
    >
      <transition
        name="badge-fade"
        mode="out-in"
      >
        <v-badge
          v-if="pendingSpd > 0"
          :key="pendingSpd"
          :color="item.badgeColor"
          inline
          :content="pendingSpd"
          :value="pendingSpd"
        >
        </v-badge>
      </transition>
    </v-list-item-action>
    <v-list-item-action
      v-else-if="item.title === 'Calendar' && item.badge && item.show.includes(userRole) && ['Auditor', 'Secretary'].includes(userRole)"
      class="flex-shrink-0"
    >
      <transition
        name="badge-fade"
        mode="out-in"
      >
        <v-badge
          v-if="pendingLeaveRequests > 0"
          :key="pendingLeaveRequests"
          :color="item.badgeColor"
          inline
          :content="pendingLeaveRequests"
          :value="pendingLeaveRequests"
        >
        </v-badge>
      </transition>
    </v-list-item-action>
    <v-list-item-action
      v-else-if="item.title === 'Calendar' && item.badge && item.show.includes(userRole) && ['Clerk', 'SPD reporter'].includes(userRole)"
      class="flex-shrink-0"
    >
      <transition
        name="badge-fade"
        mode="out-in"
      >
        <v-badge
          v-if="pendingLeaveInvites > 0"
          :key="pendingLeaveInvites"
          :color="item.badgeColor"
          inline
          :content="pendingLeaveInvites"
          :value="pendingLeaveInvites"
        >
        </v-badge>
      </transition>
    </v-list-item-action>
    <v-list-item-action
      v-else-if="item.title == 'New leads' && item.badge"
      class="flex-shrink-0"
    >
      <transition
        name="badge-fade"
        mode="out-in"
      >
        <v-badge
          v-if="newLeads > 0"
          :key="newLeads"
          :color="item.badgeColor"
          inline
          :content="newLeads"
          :value="newLeads"
        >
        </v-badge>
      </transition>
    </v-list-item-action>
    <v-list-item-action
      v-else-if="item.title == 'Quoted leads' && item.badge"
      class="flex-shrink-0"
    >
      <transition
        name="badge-fade"
        mode="out-in"
      >
        <v-badge
          v-if="quotedLeads > 0"
          :key="quotedLeads"
          :color="item.badgeColor"
          inline
          :content="quotedLeads"
          :value="quotedLeads"
        >
        </v-badge>
      </transition>
    </v-list-item-action>
    <v-list-item-action
      v-else-if="item.title == 'Issue leads' && item.badge"
      class="flex-shrink-0"
    >
      <transition
        name="badge-fade"
        mode="out-in"
      >
        <v-badge
          v-if="issueLeads > 0"
          :key="issueLeads"
          :color="item.badgeColor"
          inline
          :content="issueLeads"
          :value="issueLeads"
        >
        </v-badge>
      </transition>
    </v-list-item-action>
  </v-list-item>
</template>

<script>
import {
  inject,
  computed,
} from '@vue/composition-api'
import themeConfig from '@themeConfig'
import { useUtils } from '@core/libs/i18n'
import { useUtils as useAclUtils } from '@core/libs/acl'
import useVerticalNavLink from '@core/layouts/composable/vertical-nav/useVerticalNavLink'

export default {
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const userData = JSON.parse(localStorage.getItem('userData'))
    const userRole = userData && userData.role ? userData.role : null
    const store = inject('store')
    const { isActive, linkProps } = useVerticalNavLink(props.item)
    const { t } = useUtils()
    const { canViewVerticalNavMenuLink } = useAclUtils()

    const pendingTotal = computed(() => store.state.pendingRequests.pending_total)
    const pendingSpd = computed(() => store.state.pendingSpd)
    const pendingLeaveInvites = computed(() => store.state.pendingLeaveInvites)
    const pendingLeaveRequests = computed(() => store.state.pendingLeaveRequests)
    const newLeads = computed(() => store.state.leadCounts.new_leads)
    const quotedLeads = computed(() => store.state.leadCounts.quoted_leads)
    const issueLeads = computed(() => store.state.leadCounts.issue_leads)

    return {
      userRole,
      pendingTotal,
      pendingSpd,
      pendingLeaveInvites,
      pendingLeaveRequests,
      newLeads,
      quotedLeads,
      issueLeads,
      isActive,
      linkProps,
      alternateIcon: themeConfig.menu.groupChildIcon,

      // i18n
      t,

      // ACL
      canViewVerticalNavMenuLink,
    }
  },
}
</script>

<style lang="scss">
@import '~vuetify/src/styles/styles.sass';

.vertical-nav-menu-link {
  &.v-list-item--active {
    box-shadow: 0 5px 10px -4px rgba(94, 86, 105, 0.42);
    @include elevationTransition();
  }
}
.badge-fade-enter-active,
.badge-fade-leave-active {
  transition: all 0.4s ease;
}
.badge-fade-enter {
  transform: scale(1.3);
  opacity: 0;
}
.badge-fade-enter-to {
  transform: scale(1);
  opacity: 1;
  animation: bounce 0.4s ease;
}
.badge-fade-leave {
  transform: scale(1);
  opacity: 1;
}
.badge-fade-leave-to {
  transform: scale(0.8);
  opacity: 0;
}

@keyframes bounce {
  0% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.4);
  }
  60% {
    transform: scale(0.8);
  }
  100% {
    transform: scale(1);
  }
}
</style>
